/**
 * Shared pieces of the app's full-text search: the excerpt parser, the
 * markdown clean-up that excerpt needs, and the two timing constants every
 * search box must agree on.
 *
 * These grew inside the command palette (FRONT-872/874) and moved here when
 * the phone surface's sheet search needed the same behaviour (FRONT-1317
 * follow-up). Two search boxes debouncing differently, or one printing raw
 * `<b>` tags the other renders as highlights, is exactly the drift this
 * prevents — and the phone must not pull the whole desktop palette module
 * into its bundle just to reuse four pure things. The palette re-exports
 * them, so its own imports and tests are unchanged.
 */

/** Content search only fires once the (debounced) query reaches this length —
 * a single character against chats/files/documents/memory would be too noisy
 * and too expensive to be worth the round trip. */
export const MIN_CONTENT_QUERY_LENGTH = 2;
export const CONTENT_SEARCH_DEBOUNCE_MS = 250;

export interface SnippetSegment {
  text: string;
  highlighted: boolean;
}

/**
 * Pure: parse a search snippet (Postgres `ts_headline` output, which wraps
 * matched terms in `<b>…</b>`) into typed segments so callers can render the
 * highlight safely through React elements — NEVER via
 * `dangerouslySetInnerHTML`. Used by the palette's list rows (which just
 * concatenate `.text`, ignoring `.highlighted`), its preview pane, and the
 * phone surface's sheet rows (which render `.highlighted` as a mark).
 *
 * FRONT-874: replaces the old `stripSnippetMarkup` (which only stripped the
 * tags for a plain secondary line) now that the preview pane needs the
 * highlight itself, not just plain text.
 *
 * Degrades gracefully on a malformed/unclosed `<b>` — everything from an
 * unclosed tag to the end of the string is treated as highlighted rather
 * than leaking literal tag characters into the rendered text.
 */
export function parseSnippet(snippet: string): SnippetSegment[] {
  const parts = snippet.split(/(<\/?b>)/g);
  const segments: SnippetSegment[] = [];
  let highlighted = false;
  for (const part of parts) {
    if (part === '<b>') {
      highlighted = true;
      continue;
    }
    if (part === '</b>') {
      highlighted = false;
      continue;
    }
    if (part === '') continue;
    segments.push({ text: part, highlighted });
  }
  return segments;
}

/**
 * Pure: strips the markdown junk that leaks into CHAT search snippets. Chat
 * snippets come from Postgres `ts_headline` run over the raw markdown chat
 * message body, so a mid-text fragment carries stray syntax that was never
 * meant to be visible — heading hashes, `---` rules, `**` pairs, stray
 * backticks. `stripMarkdownPeek` (in the palette) already does this for
 * document peeks; chats never got the same treatment (FRONT-874 fidelity fix).
 *
 * Deliberately conservative, and NOT anchored to line/string boundaries the
 * way `stripMarkdownPeek` is — a snippet is an arbitrary mid-text window, so
 * e.g. a heading can appear with no preceding line break at all
 * (`"independent.# 🧠 Quantum"` → `"independent. 🧠 Quantum"`: only the `#`
 * itself is stripped, the single space either side of it is left alone).
 * Only strips a hash-run immediately followed by whitespace (never `C#` or
 * `#tag`, which have no space after the `#`) and a `---` run bounded by
 * whitespace or a string edge (never a mid-word `a---b`).
 *
 * Meant to run on a single already-`parseSnippet`-split segment's plain text
 * (never the raw `<b>`-tagged string) so highlight boundaries are untouched —
 * callers clean each segment independently and re-render them, never re-parse
 * the cleaned output for markup.
 */
export function cleanSnippetText(s: string): string {
  return s
    .replace(/#{1,6}(?=\s)/g, '')
    .replace(/(?<=^|\s)---(?=\s|$)/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*\*/g, '')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/`/g, '')
    .replace(/ {2,}/g, ' ');
}
